import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import apiClient from '../utils/apiClient';
import {
  comparisonCellKey,
  formatComparisonDelta,
  formatComparisonPrice,
  indexComparisonCells,
  isSelectedComparisonCell,
  isShopComparisonResponse,
} from '../utils/shopComparisonMatrix';

const stickyCell = {
  position: 'sticky',
  left: 0,
  bgcolor: 'background.paper',
  borderRight: 1,
  borderColor: 'divider',
};

function ShopComparisonMatrixDialog({ calculationContext }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [matrix, setMatrix] = useState(null);
  const cacheRef = useRef(new Map());
  const abortRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    requestIdRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    cacheRef.current.clear();
    setOpen(false);
    setLoading(false);
    setError('');
    setMatrix(null);
  }, [calculationContext?.cacheKey]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const cellsByKey = useMemo(
    () => indexComparisonCells(matrix?.cells),
    [matrix],
  );

  if (!calculationContext) return null;

  const handleOpen = async () => {
    setOpen(true);
    setError('');

    const cached = cacheRef.current.get(calculationContext.cacheKey);
    if (cached) {
      setMatrix(cached);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    abortRef.current = controller;
    setLoading(true);
    setMatrix(null);

    try {
      const response = await apiClient.post(
        `/products/${encodeURIComponent(calculationContext.productId)}/comparison-matrix`,
        calculationContext.body,
        { signal: controller.signal },
      );
      if (requestId !== requestIdRef.current) return;
      if (!isShopComparisonResponse(response.data)) {
        throw new Error('Invalid comparison response');
      }
      cacheRef.current.set(calculationContext.cacheKey, response.data);
      setMatrix(response.data);
    } catch (requestError) {
      if (controller.signal.aborted || requestId !== requestIdRef.current) return;
      setError(
        requestError.response?.data?.message
          || 'Не удалось загрузить сравнение. Закройте окно и попробуйте снова.',
      );
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        abortRef.current = null;
      }
    }
  };

  const handleClose = () => setOpen(false);

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleOpen}
        sx={{ mt: 2, textTransform: 'none' }}>
        Сравнить все сочетания
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pr: 7 }}>
          Сравнение всех сочетаний
          <IconButton
            aria-label="Закрыть"
            onClick={handleClose}
            sx={{ position: 'absolute', right: 12, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress aria-label="Загрузка сравнения" />
            </Box>
          )}

          {!loading && error && <Alert severity="error">{error}</Alert>}

          {!loading && !error && matrix && (
            <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table stickyHeader size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...stickyCell, zIndex: 4, minWidth: 180 }}>
                      Тип стекла
                    </TableCell>
                    {matrix.axes.hardware_sets.map((hardwareSet) => (
                      <TableCell
                        key={hardwareSet.id}
                        align="center"
                        sx={{ minWidth: 150, whiteSpace: 'normal' }}>
                        {hardwareSet.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matrix.axes.glass_types.map((glassType) => (
                    <TableRow key={glassType.id}>
                      <TableCell component="th" scope="row" sx={{ ...stickyCell, zIndex: 2 }}>
                        {glassType.name}
                      </TableCell>
                      {matrix.axes.hardware_sets.map((hardwareSet) => {
                        const cell = cellsByKey.get(
                          comparisonCellKey(glassType.id, hardwareSet.id),
                        );
                        const selected = isSelectedComparisonCell(cell, matrix.selected);
                        const available = cell?.status === 'available';
                        return (
                          <TableCell
                            key={hardwareSet.id}
                            align="center"
                            sx={{
                              bgcolor: selected ? 'primary.50' : undefined,
                              verticalAlign: 'top',
                            }}>
                            {selected ? (
                              <>
                                <Typography variant="body2" fontWeight={700} color="primary.main">
                                  Ваш выбор
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatComparisonPrice(cell.dealer_customer_price)}
                                </Typography>
                              </>
                            ) : available ? (
                              <>
                                <Typography variant="body2" fontWeight={600}>
                                  {formatComparisonPrice(cell.dealer_customer_price)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatComparisonDelta(
                                    cell.dealer_customer_price,
                                    matrix.selected.dealer_customer_price,
                                  )}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Недоступно
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ShopComparisonMatrixDialog;
